if (!customElements.get('ease-sticky-atc-bar')) {
  customElements.define(
    'ease-sticky-atc-bar',
    class EaseStickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('ease-sticky-atc-bar-enabled');

        this.selectors = {
          variantIdSelect: '[name="id"]',
        };
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get quantity() {
        return this.querySelector('quantity-input');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get select() {
        return this.querySelector('select[name="id"]');
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get drawerMode() {
        return this.hasAttribute('data-ease-sticky-drawer-mode');
      }

      get simplePdpRoot() {
        return document.querySelector('[data-ease-simple-pdp]');
      }

      get mainProductForm() {
        return document.querySelector('[data-ease-simple-pdp-form]');
      }

      get mainAtcButton() {
        return document.querySelector('[data-ease-simple-pdp-form] [data-ease-cta]');
      }

      get mainCtaLabel() {
        return document.querySelector('[data-ease-simple-pdp-form] [data-ease-cta-label]');
      }

      easeScrollToTarget(targetId) {
        if (!targetId) return;
        const anchorEl =
          document.getElementById(targetId) ||
          document.querySelector(`[id^="${targetId}"]`) ||
          document.querySelector('#ease-product-info-target') ||
          document.querySelector('product-info[data-section="main"]') ||
          document.querySelector('product-info[id^="MainProduct-"]');
        if (!anchorEl) return;

        const productInfo = anchorEl.closest?.('product-info') || anchorEl;
        const mediaTopEl =
          productInfo?.querySelector?.('media-gallery') ||
          productInfo?.querySelector?.('.product__media-wrapper') ||
          anchorEl;

        const prefersReduced =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReduced) {
          mediaTopEl.scrollIntoView({ block: 'start' });
          return;
        }

        const start = window.pageYOffset || document.documentElement.scrollTop || 0;
        const rect = mediaTopEl.getBoundingClientRect();
        const target = Math.max(0, start + rect.top - 100);
        const distance = target - start;
        if (Math.abs(distance) < 2) return;

        const duration = Math.min(1200, Math.max(700, Math.abs(distance) * 0.6));

        function easeInOutCubic(t) {
          return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        }

        const t0 = performance.now();
        function step(now) {
          const p = Math.min(1, (now - t0) / duration);
          const y = start + distance * easeInOutCubic(p);
          window.scrollTo(0, y);
          if (p < 1) window.requestAnimationFrame(step);
        }
        window.requestAnimationFrame(step);
      }

      getStickyButtonLabel() {
        const addButton = this.productForm?.querySelector('[name="add"]');
        const raw = addButton?.dataset?.easeStickyAtcLabel;
        return raw && String(raw).trim() ? String(raw).trim() : FoxTheme.variantStrings.addToCart;
      }

      connectedCallback() {
        this.mainProductInfo =
          document.querySelector('product-info.ease-simple-pdp') || document.querySelector('product-info');
        this.container = this.closest('.ease-sticky-atc-bar');
        this.scrollButton = this.querySelector('[data-ease-sticky-drawer-trigger], [data-ease-scroll-target]');
        this.stickyPriceChip = this.querySelector('[data-ease-sticky-price]');
        this.drawer = this.drawerMode ? document.getElementById(this.dataset.drawerId) : null;
        this.drawerPickerSlot = this.drawer?.querySelector('[data-ease-sticky-drawer-picker]');
        this.drawerSubmit = this.querySelector('[data-ease-sticky-drawer-submit]');
        this.drawerCtaLabel = this.querySelector('[data-ease-sticky-drawer-cta-label]');
        this.drawerSave = this.querySelector('[data-ease-sticky-drawer-save]');
        this.drawerSaveWas = this.querySelector('[data-ease-sticky-drawer-save-was]');
        this.drawerSaveOff = this.querySelector('[data-ease-sticky-drawer-save-off]');

        this.variantData = this.getVariantData();
        const vid = this.querySelector(this.selectors.variantIdSelect)?.value;
        if (vid) {
          this.currentVariant = this.variantData.find((v) => v.id === Number(vid));
        }

        this.init();

        if (this.drawerMode && this.drawer) {
          this.initDrawer();
        } else if (this.scrollButton) {
          this.scrollButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.easeScrollToTarget(this.scrollButton.getAttribute('data-ease-scroll-target'));
          });
        }

        this.select?.addEventListener('change', () => {
          this.updateQuantityInput();
          this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
          const selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;
          this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));

          if (this.mainVariantSelects) {
            Array.from(this.mainVariantSelects.querySelectorAll('select, fieldset'), (element, index) => {
              const variantOptionVal = this.currentVariant.options[index];

              switch (element.tagName) {
                case 'SELECT':
                  element.value = variantOptionVal;
                  break;
                case 'FIELDSET':
                  Array.from(element.querySelectorAll('input')).forEach((radio) => {
                    if (radio.value === variantOptionVal) {
                      radio.checked = true;
                    }
                  });
                  break;
              }
            });
            this.mainVariantSelects.dispatchEvent(new Event('change'));
          }

          this.updatePrice();
          this.updateButton(true, '', false);
          if (!this.currentVariant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }

      lockBackgroundScroll() {
        if (this._bgScrollLocked) return;
        this._bgScrollY = window.scrollY || document.documentElement.scrollTop || 0;
        this._bgScrollLocked = true;
        document.body.classList.add('ease-sticky-drawer-open');
        document.body.style.setProperty('--ease-sticky-drawer-scroll-y', `-${this._bgScrollY}px`);
        this.lockBuyboxLayout();

        if (!this._onDrawerBackgroundScroll) {
          this._onDrawerBackgroundScroll = () => {
            if (!this._bgScrollLocked) return;
            const y = this._bgScrollY;
            if (Math.abs((window.scrollY || 0) - y) > 1) {
              window.scrollTo(0, y);
            }
          };
          this._onDrawerBackgroundFocus = () => {
            if (!this._bgScrollLocked) return;
            window.requestAnimationFrame(() => this.restoreBackgroundScrollPosition());
          };
        }

        window.addEventListener('scroll', this._onDrawerBackgroundScroll, { passive: true });
        document.addEventListener('focusin', this._onDrawerBackgroundFocus);
      }

      restoreBackgroundScrollPosition() {
        if (!this._bgScrollLocked) return;
        window.scrollTo(0, this._bgScrollY);
      }

      lockBuyboxLayout() {
        const buybox = document.querySelector('product-info.ease-simple-pdp');
        if (!buybox || this._buyboxLocked) return;
        this._buyboxLocked = true;
        this._buyboxLockHeight = buybox.offsetHeight;
        buybox.style.minHeight = `${this._buyboxLockHeight}px`;
      }

      unlockBuyboxLayout() {
        const buybox = document.querySelector('product-info.ease-simple-pdp');
        if (buybox) buybox.style.minHeight = '';
        this._buyboxLocked = false;
        this._buyboxLockHeight = null;
      }

      unlockBackgroundScroll() {
        if (!this._bgScrollLocked) return;
        const y = this._bgScrollY || 0;
        this._bgScrollLocked = false;
        document.body.classList.remove('ease-sticky-drawer-open');
        document.body.style.removeProperty('--ease-sticky-drawer-scroll-y');
        window.removeEventListener('scroll', this._onDrawerBackgroundScroll);
        document.removeEventListener('focusin', this._onDrawerBackgroundFocus);
        this.unlockBuyboxLayout();
        document.dispatchEvent(new CustomEvent('ease-sticky-drawer-closed'));
        window.scrollTo(0, y);
      }

      runWithPreservedBackgroundScroll(fn) {
        const y = this._bgScrollLocked ? this._bgScrollY : window.scrollY || document.documentElement.scrollTop || 0;
        fn();
        [0, 50, 150, 350, 600].forEach((delay) => {
          window.setTimeout(() => {
            if (!this.drawer?.open) return;
            window.scrollTo(0, y);
          }, delay);
        });
      }

      prefersReducedMotion() {
        return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      }

      setDrawerCtaFlip(isOpen) {
        const flipInner = this.querySelector('[data-ease-sticky-btn-flip] .ease-sticky-atc-bar__btn-flip-inner');
        const useFlip = flipInner && !this.prefersReducedMotion();

        if (useFlip) {
          flipInner.classList.toggle('is-flipped', isOpen);
          if (this.scrollButton) {
            this.scrollButton.hidden = false;
            this.scrollButton.tabIndex = isOpen ? -1 : 0;
            this.scrollButton.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
            this.scrollButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          }
          if (this.drawerSubmit) {
            this.drawerSubmit.hidden = false;
            this.drawerSubmit.tabIndex = isOpen ? 0 : -1;
            this.drawerSubmit.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
          }
          return;
        }

        if (flipInner) {
          flipInner.classList.remove('is-flipped');
        }
        if (this.scrollButton) {
          this.scrollButton.hidden = isOpen;
          this.scrollButton.tabIndex = 0;
          this.scrollButton.removeAttribute('aria-hidden');
          this.scrollButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }
        if (this.drawerSubmit) {
          this.drawerSubmit.hidden = !isOpen;
          this.drawerSubmit.tabIndex = isOpen ? 0 : -1;
          if (isOpen) {
            this.drawerSubmit.removeAttribute('aria-hidden');
          } else {
            this.drawerSubmit.setAttribute('aria-hidden', 'true');
          }
        }
      }

      setDrawerCtaMode(isOpen) {
        if (!this.container) return;
        this.container.classList.toggle('ease-sticky-atc-bar--drawer-open', isOpen);
        document.body.classList.toggle('ease-sticky-drawer-cta-active', isOpen);
        this.setDrawerCtaFlip(isOpen);
        this.updateDrawerAndStickySummary();
      }

      beginDrawerClose() {
        if (!this.drawer) return;
        this.drawer.classList.add('ease-sticky-atc-drawer--closing');
        this.setDrawerCtaFlip(false);
      }

      finishDrawerClose() {
        if (!this.drawer) return;
        this.drawer.classList.remove('ease-sticky-atc-drawer--closing');
        this.setDrawerCtaMode(false);
        this.unlockBackgroundScroll();
        document.body.classList.remove('ease-sticky-drawer-open');
        document.body.classList.remove('ease-sticky-drawer-cta-active');
        document.body.style.removeProperty('--ease-sticky-drawer-scroll-y');
        document.body.classList.remove('modal-show');
      }

      initDrawer() {
        if (!this.drawer || !this.simplePdpRoot) return;

        if (this.scrollButton) {
          this.scrollButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.drawer.classList.remove('ease-sticky-atc-drawer--closing');
            this.lockBackgroundScroll();
            this.setDrawerCtaMode(true);
            this.refreshDrawerPicker();
            if (typeof this.drawer.show === 'function') {
              this.drawer.show(this.scrollButton);
            } else {
              this.drawer.setAttribute('open', '');
            }
            document.body.classList.add('modal-show');
          });
        }

        if (this.drawerSubmit) {
          this.drawerSubmit.addEventListener('click', () => {
            const mainForm = this.mainProductForm;
            const mainBtn = this.mainAtcButton;
            if (!mainBtn || mainBtn.disabled || !mainForm) return;
            if (typeof this.drawer.hide === 'function') {
              this.drawer.hide();
            } else {
              this.drawer.removeAttribute('open');
            }
            if (typeof mainForm.requestSubmit === 'function') {
              mainForm.requestSubmit(mainBtn);
            } else {
              mainBtn.click();
            }
          });
        }

        this.drawer.addEventListener('modal:handleAfterShow', () => {
          this.drawer.classList.remove('ease-sticky-atc-drawer--closing');
          this.lockBackgroundScroll();
          this.setDrawerCtaMode(true);
        });

        this.drawer.addEventListener('modal:handleAfterHide', () => {
          this.finishDrawerClose();
        });

        this.drawer.addEventListener('toggle', () => {
          if (this.drawer.open) {
            this.drawer.classList.remove('ease-sticky-atc-drawer--closing');
            this.lockBackgroundScroll();
            this.setDrawerCtaMode(true);
            return;
          }
          this.beginDrawerClose();
        });

        this.updateDrawerAndStickySummary();
      }

      getMainPickerSource() {
        return this.simplePdpRoot?.querySelector('.ease-simple-pdp__core');
      }

      sanitizeDrawerClone(wrap) {
        wrap.querySelectorAll('[id]').forEach((el) => {
          const oldId = el.id;
          if (!oldId) return;
          const newId = `ease-sticky-drawer-${oldId}`;
          el.id = newId;
          wrap.querySelectorAll(`[for="${CSS.escape(oldId)}"]`).forEach((node) => {
            node.setAttribute('for', newId);
          });
        });

        wrap.querySelectorAll('input, select, textarea').forEach((el) => {
          el.removeAttribute('name');
          el.removeAttribute('form');
        });

        wrap.querySelectorAll('variant-selects, script').forEach((el) => el.remove());
      }

      refreshDrawerPicker() {
        const source = this.getMainPickerSource();
        if (!source || !this.drawerPickerSlot) return;

        const wrap = document.createElement('div');
        wrap.className = 'ease-sticky-picker-trimmed';

        const tierLabel = source.querySelector('.ease-simple-pdp__blocklabel');
        const tiers = source.querySelector('.ease-simple-pdp__tiers');
        const colorFieldset = source.querySelector('.product-form__input--color');

        if (tierLabel) wrap.appendChild(tierLabel.cloneNode(true));
        if (tiers) wrap.appendChild(tiers.cloneNode(true));
        if (colorFieldset) {
          const colorBlock = document.createElement('div');
          colorBlock.className = 'ease-simple-pdp__color-wrap ease-simple-pdp__color-wrap--drawer';
          colorBlock.appendChild(colorFieldset.cloneNode(true));
          wrap.appendChild(colorBlock);
        }

        this.sanitizeDrawerClone(wrap);
        this.drawerPickerSlot.innerHTML = '';
        this.drawerPickerSlot.appendChild(wrap);
        this.syncDrawerPickerFromMain();
        this.bindDrawerPicker();
      }

      syncDrawerPickerFromMain() {
        const mainSource = this.getMainPickerSource();
        const drawerWrap = this.drawerPickerSlot?.querySelector('.ease-sticky-picker-trimmed');
        if (!mainSource || !drawerWrap) return;

        drawerWrap.querySelectorAll('[data-ease-tier]').forEach((drawerTier) => {
          const key = drawerTier.getAttribute('data-ease-tier');
          const mainTier = mainSource.querySelector(`[data-ease-tier="${key}"]`);
          if (!mainTier) return;

          drawerTier.className = mainTier.className;
          drawerTier.disabled = mainTier.disabled;

          ['data-ease-tier-price', 'data-ease-tier-compare', 'data-ease-tier-saving'].forEach((attr) => {
            const mainEl = mainTier.querySelector(`[${attr}="${key}"]`);
            const drawerEl = drawerTier.querySelector(`[${attr}="${key}"]`);
            if (!mainEl || !drawerEl) return;
            drawerEl.innerHTML = mainEl.innerHTML;
            if (mainEl.hasAttribute('hidden')) drawerEl.setAttribute('hidden', '');
            else drawerEl.removeAttribute('hidden');
          });
        });

        const mainRadios = mainSource.querySelectorAll('.product-form__input--color input[type="radio"]');
        const drawerRadios = drawerWrap.querySelectorAll('.product-form__input--color input[type="radio"]');
        drawerRadios.forEach((drawerRadio, index) => {
          const mainRadio = mainRadios[index];
          if (!mainRadio || drawerRadio.value !== mainRadio.value) return;
          drawerRadio.checked = mainRadio.checked;
        });
      }

      bindDrawerPicker() {
        if (this.drawerPickerBound || !this.drawerPickerSlot) return;

        this.drawerPickerSlot.addEventListener('click', (event) => {
          const mainSource = this.getMainPickerSource();
          if (!mainSource) return;

          const tierBtn = event.target.closest('[data-ease-tier]');
          if (tierBtn && this.drawerPickerSlot.contains(tierBtn) && !tierBtn.disabled) {
            event.preventDefault();
            const key = tierBtn.getAttribute('data-ease-tier');
            const pdpSection = document.querySelector('[data-ease-simple-pdp]');
            this.runWithPreservedBackgroundScroll(() => {
              if (typeof pdpSection?.__easeSelectTier === 'function') {
                pdpSection.__easeSelectTier(key);
              } else {
                const mainTier = mainSource.querySelector(`[data-ease-tier="${key}"]`);
                if (mainTier) mainTier.click();
              }
            });
            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(() => {
                if (typeof pdpSection?.__easeSimplePdpFlush === 'function') {
                  pdpSection.__easeSimplePdpFlush();
                }
                this.syncDrawerPickerFromMain();
                this.updateDrawerAndStickySummary();
                this.restoreBackgroundScrollPosition();
              });
            });
            return;
          }

          const colorLabel = event.target.closest('.product-form__input--color label');
          if (colorLabel && this.drawerPickerSlot.contains(colorLabel)) {
            const drawerRadio =
              colorLabel.control ||
              (colorLabel.htmlFor
                ? this.drawerPickerSlot.querySelector(`#${CSS.escape(colorLabel.htmlFor)}`)
                : null);
            if (!drawerRadio) return;

            const mainRadio = mainSource.querySelector(
              `.product-form__input--color input[type="radio"][value="${CSS.escape(drawerRadio.value)}"]`
            );
            if (mainRadio && !mainRadio.checked) {
              this.runWithPreservedBackgroundScroll(() => {
                mainRadio.checked = true;
                mainRadio.dispatchEvent(new Event('change', { bubbles: true }));
              });
            }
            window.setTimeout(() => {
              this.syncDrawerPickerFromMain();
              this.updateDrawerAndStickySummary();
              this.restoreBackgroundScrollPosition();
            }, 140);
          }
        });

        this.drawerPickerBound = true;
      }

      updateDrawerAndStickySummary() {
        const pdpSection = document.querySelector('[data-ease-simple-pdp]');
        const labelText =
          (typeof pdpSection?.__easeSimplePdpGetCtaLabel === 'function' && pdpSection.__easeSimplePdpGetCtaLabel()) ||
          this.mainCtaLabel?.textContent?.trim() ||
          '';

        if (this.drawerCtaLabel) {
          this.drawerCtaLabel.textContent = labelText || this.drawerCtaLabel.textContent;
        }

        // Ersparnis links im Button. Die PDP rechnet und formatiert, damit hier
        // keine zweite Waehrungslogik entsteht. Ohne Vergleichspreis, etwa in
        // Maerkten ohne gepflegte UVP, bleibt der Block aus.
        if (this.drawerSave) {
          const savings =
            typeof pdpSection?.__easeSimplePdpGetSavings === 'function'
              ? pdpSection.__easeSimplePdpGetSavings()
              : null;
          if (savings) {
            if (this.drawerSaveWas) this.drawerSaveWas.textContent = savings.compare;
            if (this.drawerSaveOff) this.drawerSaveOff.textContent = `−${savings.saved}`;
          }
          this.drawerSave.hidden = !savings;
        }

        if (this.drawerSubmit && this.mainAtcButton) {
          this.drawerSubmit.disabled = !!this.mainAtcButton.disabled;
        }
      }

      updateQuantityInput() {
        const selectedVariantId = this.querySelector(this.selectors.variantIdSelect)?.value;
        this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));
        const qtyHost = document.querySelector(`#QuantitySticky-${this.sectionId}`);
        if (!qtyHost || !this.currentVariant) return;

        fetch(`${this.getAttribute('data-url')}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const quantity = document.querySelector(`#QuantitySticky-${this.sectionId}`);
            const newQuantity = parsedHTML.querySelector(`#QuantitySticky-${this.sectionId}`);
            if (quantity && newQuantity) {
              quantity.innerHTML = newQuantity.innerHTML;
            }
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {});
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      init() {
        const heroCta = document.querySelector(
          '.ease-salespage-hero-hook-section .ease-sp-hero-hook__cta[data-ease-scroll-target]'
        );
        const productInfoEl =
          document.querySelector('product-info[data-section="main"]') ||
          document.querySelector('product-info[id^="MainProduct-"]') ||
          document.querySelector('product-info.ease-simple-pdp');

        if (!heroCta && !productInfoEl) {
          this.container.classList.add('ease-sticky-atc-bar--show');
          this.syncWithMainProductForm();
          return;
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        this.heroCtaInView = !!heroCta;
        this.productInfoInView = false;
        this.hasPassedHeroCta = false;

        const updateStickyVisibility = () => {
          const shouldShow = this.hasPassedHeroCta && !this.heroCtaInView && !this.productInfoInView;
          this.container.classList.toggle('ease-sticky-atc-bar--show', shouldShow);
        };

        if (heroCta) {
          this.heroObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this.heroCtaInView = entry.isIntersecting;
                if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
                  this.hasPassedHeroCta = true;
                }
                if (entry.isIntersecting) {
                  this.hasPassedHeroCta = false;
                }
              });
              updateStickyVisibility();
            },
            { threshold: 0, rootMargin: '0px 0px 0px 0px' }
          );
          this.heroObserver.observe(heroCta);
        } else {
          this.heroCtaInView = false;
          this.hasPassedHeroCta = true;
        }

        if (productInfoEl) {
          this.productObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this.productInfoInView = entry.isIntersecting;
              });
              updateStickyVisibility();
            },
            { threshold: 0.12, rootMargin: '-10% 0px -10% 0px' }
          );
          this.productObserver.observe(productInfoEl);
        }

        updateStickyVisibility();

        this.syncWithMainProductForm();
      }

      checkDevice() {
        document.documentElement.style.setProperty('--ease-sticky-atc-bar-height', this.clientHeight + 'px');
      }

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.productForm;
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        const stickyLabel = this.getStickyButtonLabel();
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = stickyLabel;
        }
      }

      updatePrice() {
        const root = this.querySelector('.ease-sticky-atc-bar__price');
        if (!root || !this.currentVariant) return;

        const priceWrapper = root.querySelector('.price');
        if (!priceWrapper) return;

        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { compare_at_price, price, available } = this.currentVariant;
        const onSale = !!(compare_at_price && compare_at_price > price);

        priceWrapper.classList.toggle('price--on-sale', onSale);
        priceWrapper.classList.toggle('price--sold-out', !available);

        const regularEl = root.querySelector('.price__regular .price-item--regular');
        const saleValueEl = root.querySelector('.ease-sticky-atc-price__sale-value');
        const compareEl = root.querySelector('.price__sale s.price-item--regular');

        if (regularEl) {
          regularEl.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);
        }
        if (saleValueEl) {
          saleValueEl.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);
        }
        if (compareEl) {
          compareEl.innerHTML = onSale
            ? FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)
            : FoxTheme.Currency.formatMoney(price, moneyFormat);
        }
      }

      syncWithMainProductForm() {
        if (!this.mainProductInfo && !this.drawerMode) return;

        const onVariantSync = () => {
          this.updateDrawerAndStickySummary();
          if (this.drawer?.open) {
            this.syncDrawerPickerFromMain();
          }
        };

        if (this.drawerMode) {
          const observer = new MutationObserver(onVariantSync);
          if (this.mainCtaLabel) {
            observer.observe(this.mainCtaLabel, { childList: true, characterData: true, subtree: true });
          }
          onVariantSync();
        }

        if (!this.mainProductInfo) return;

        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          const variantInput = this.querySelector('[name="id"]');

          this.currentVariant = variant;
          if (variantInput) variantInput.value = variant.id;
          this.updatePrice();
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }

          this.updateQuantityInput();
          onVariantSync();
        });
      }
    }
  );
}
